import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import "./MetaDataWidget.scss";
import { percentageCalculation } from "../../../components/common/utilities";
import { useSelector } from "react-redux";

export default function MetaDataWidget({ data }) {
  const total = useSelector((state) => state.dataTagReducer.totalFiles);

  const fields = [
    { key: "mimeTypeCount", label: "Mime Type" },
    { key: "fieldHashCount", label: "Hash" },
    { key: "createTimeCount", label: "Creation Time" },
    { key: "directoryNameCount", label: "Directory Name" },
    { key: "fileSizeCount", label: "File Size" },
    { key: "fieldOwnerCount", label: "Owner" },
    { key: "containerCount", label: "Personal/Shared" },
  ];

  /**
   * Build chart data
   * NOTE:
   * We intentionally SWAP the values of "done" and "remaining"
   * so that:
   * - Done bar shows REMAINING percentage
   * - Remaining bar shows DONE percentage
   * Tooltip logic depends on this swap.
   */
  const chartData = fields.map((f) => {
    const doneCount = data[f.key] || 0;
    const unprocessedCount = data.unprocessedFileCount || 0;

    const donePct =
      total > 0
        ? percentageCalculation([{ count: doneCount }], total)[0]?.count || 0
        : 0;

    const unprocessedPct =
      total > 0
        ? percentageCalculation([{ count: unprocessedCount }], total)[0]
            ?.count || 0
        : 0;

    const remainingPct = Math.max(100 - (donePct + unprocessedPct), 0);
    const remainingCount = total - (doneCount + unprocessedCount);

    return {
      name: f.label,

      // SWAPPED VALUES (IMPORTANT)
      // done → shows REMAINING %
      // remaining → shows DONE %
      done: remainingPct,
      remaining: donePct,
      unprocessed: unprocessedPct,

      //  SWAPPED COUNTS (used in tooltip)
      doneCount: remainingCount,
      remainingCount: doneCount,
      unprocessedCount,
    };
  });

  const getRadius = (row, key) => {
    if (row[key] === 0) return [0, 0, 0, 0];

    if (key === "done") return [10, 10, 10, 10];

    const activeBars = [
      { key: "done", value: row.remaining },
      { key: "unprocessed", value: row.unprocessed },
      { key: "remaining", value: row.done },
    ].filter((b) => b.value > 0);

    const topKey = activeBars[activeBars.length - 1]?.key;
    if (key === topKey) return [10, 10, 0, 0];

    return [0, 0, 0, 0];
  };

  return (
    <div className="metadata-widget">
      <h4 className="widget-title">Metadata Hygiene</h4>

      <div className="stack-chart-container">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} barCategoryGap={20}>
            <XAxis
              dataKey="name"
              height={80}
              tick={<SlantedTick />}
              interval={0}
              axisLine={false}
              tickLine={false}
            />

            <Tooltip
              formatter={(value, name, props) => {
                const payload = props.payload;
                if (!payload) return [value, name];

                if (name === "done")
                  return [payload.doneCount, "Complete"];
                if (name === "remaining")
                  return [payload.remainingCount, "Not Available"];
                if (name === "unprocessed")
                  return [payload.unprocessedCount, "Not Processed"];

                return [value, name];
              }}
            />

            {/* Done (shows Remaining %) */}
            <Bar dataKey="done" stackId="a" fill="#1E90FF" barSize={18}>
              {chartData.map((row, i) => (
                <Cell key={i} radius={[0, 0, 10, 10]} />
              ))}
            </Bar>

            {/* Unprocessed */}
            <Bar
              dataKey="unprocessed"
              stackId="a"
              fill="#FFCC00"
              barSize={18}
            >
              {chartData.map((row, i) => (
                <Cell key={i} radius={getRadius(row, "unprocessed")} />
              ))}
            </Bar>

            {/* Remaining (shows Done %) */}
            <Bar
              dataKey="remaining"
              stackId="a"
              fill="#999999"
              barSize={18}
            >
              {chartData.map((row, i) => (
                <Cell key={i} radius={getRadius(row, "remaining")} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const SlantedTick = ({ x, y, payload }) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={0}
      y={0}
      dy={15}
      textAnchor="end"
      fontSize={10}
      transform="rotate(-40)"
      fill="#555"
    >
      {payload.value}
    </text>
  </g>
);
